/**
 * Leaflet map picker synced with ACF lat/longitude number inputs.
 *
 * Activates on any page that has ACF fields with data-name="latitude" and
 * data-name="longitude". Leaflet is loaded as a vendor script by WordPress
 * (conditionally, on pages that have these fields) and available as window.L.
 *
 * Behavior:
 *   - If lat/lng are already populated (e.g. from prefill), the marker starts
 *     there. Otherwise the map centers on continental US with no marker.
 *   - Clicking the map places/moves the marker and updates the inputs.
 *   - Dragging the marker updates the inputs.
 *   - Editing either number input and tabbing out moves the marker.
 *   - The search box geocodes via Nominatim (free, no API key required).
 *   - However the pin ends up placed (search, click, or drag), a
 *     reverse-geocode lookup fills in Country/City/State/Postal Code/County
 *     (US only) — always overwriting whatever was there, on the theory that
 *     geocoding is the authoritative source at the moment the pin moves; if
 *     the user wants something else, they can edit the field afterward.
 *     State/province/region is only auto-filled when Nominatim's response
 *     includes an ISO3166-2-lvl4 code (e.g. "US-CA"), since that code format
 *     happens to match this form's own state/province/region dropdown
 *     values exactly — no free-text-to-code reconciliation table is
 *     maintained for the cases where it's absent. County (US-only) is
 *     matched by name against whatever county_us options load for the
 *     resolved state, since that field's own list isn't coded the same way.
 *   - When lat/lng change, an AJAX proximity check runs and shows a modal
 *     if any of the current user's existing sites are within the configured
 *     radius.
 *
 * The ACF google_map field (select_location) should be hidden via the
 * "hidden-field" wrapper class in the ACF admin — it is no longer used.
 */
const { debounce } = require('../helpers/_utils');

module.exports = function init_map_latlong($) {

  if (!window.acf || !window.L) return;

  acf.addAction('ready', function () {

    const $lat_field = $('.acf-field[data-name="latitude"]');
    const $lng_field = $('.acf-field[data-name="longitude"]');
    if (!$lat_field.length || !$lng_field.length) return;

    const $lat_input = $lat_field.find('input');
    const $lng_input = $lng_field.find('input');

    // ── Build UI ─────────────────────────────────────────────────────────────

    const $lat_long_group = $lat_field.closest('.acf-field[data-type="group"]');
    const $map_row = $('<div class="mlmp-map-row"></div>');
    const $container = $('<div class="mlmp-map-container"></div>');
    const $search_row = $('<div class="mlmp-map-search"><input type="text" placeholder="Search address…" /><button type="button">Go</button></div>');
    const $map_div = $('<div class="mlmp-map-picker"></div>');

    $container.append($search_row).append($map_div);
    $lat_long_group.before($map_row);
    $map_row.append($container).append($lat_long_group);

    // ── Init map ─────────────────────────────────────────────────────────────

    const DEFAULT_LAT = 39.5;
    const DEFAULT_LNG = -98.35;
    const DEFAULT_ZOOM = 4;
    const MARKER_ZOOM = 13;

    const initial_lat = parseFloat($lat_input.val());
    const initial_lng = parseFloat($lng_input.val());
    const has_initial = !isNaN(initial_lat) && !isNaN(initial_lng);

    const map = L.map($map_div[0]).setView(
      has_initial ? [initial_lat, initial_lng] : [DEFAULT_LAT, DEFAULT_LNG],
      has_initial ? MARKER_ZOOM : DEFAULT_ZOOM
    );

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    // Leaflet computes its tile grid from the container's size at construction
    // time. When the map sits inside a field hidden by ACF conditional logic
    // (e.g. the A3 form's non-site Collection Location group, closed by
    // default), the container is 0×0 at that point, producing a broken tile
    // layout. ResizeObserver catches the container becoming visible later —
    // for whatever reason, conditional logic or otherwise — and reflows it.
    if (window.ResizeObserver) {
      const map_resize_observer = new ResizeObserver(function () {
        map.invalidateSize();
      });
      map_resize_observer.observe($map_div[0]);
    }

    let marker = null;

    function place_marker(lat, lng) {
      if (marker) {
        marker.setLatLng([lat, lng]);
      } else {
        marker = L.marker([lat, lng], { draggable: true }).addTo(map);
        marker.on('dragend', function () {
          const pos = marker.getLatLng();
          set_inputs(pos.lat, pos.lng);
          maybe_reverse_geocode(pos.lat, pos.lng);
        });
      }
    }

    // ── Reverse geocode → fill blank location fields ────────────────────────
    // Scoped to the same location group as the lat/long fields, so the
    // map/lat-long syncing itself works generically wherever this map is
    // used (Site Information, Non-Site Observations, etc.) without knowing
    // which form it's on. The country→field-name map below is specific to
    // which forms/countries this app supports, so it lives centrally in
    // MLMP\ACF_Config::ACF_STATE_FIELD_BY_COUNTRY and is passed through via
    // mlmp_settings.
    const $location_group = $lat_long_group.closest('.acf-fields');

    const STATE_FIELD_BY_COUNTRY = (typeof mlmp_settings !== 'undefined' && mlmp_settings.state_field_by_country) || {};

    function $sub_field(name) {
      return $location_group.find('.acf-field[data-name="' + name + '"]').first();
    }

    // Always sets the field from a fresh geocode result — including
    // clearing it when the result has no value for it — overwriting
    // whatever was there, on the theory that geocoding is the authoritative
    // source at the moment the pin is placed; if the user wants something
    // else, they can edit the field afterward. Uses ACF's own Field API
    // (acf.getField().val()) rather than raw jQuery .val().trigger() — for a
    // Select2-backed field (country, state/province/region), setting the
    // underlying <select>'s value directly changes it but doesn't reliably
    // refresh Select2's own rendered widget or re-run conditional logic;
    // going through ACF's field model handles both correctly, since it's
    // the same path ACF's own prefill/AJAX value-setting code uses.
    function set_field(name, value) {
      const $field = $sub_field(name);
      const field = $field.length ? acf.getField($field) : null;
      if (!field) return;
      field.val(value || '');
    }

    // County (US-only, "county_us") is a different shape entirely — its
    // options aren't static, they're loaded via AJAX (_lu-filter.js) only
    // after state_us changes, using the exact same get_filtered_lu_options
    // endpoint. Rather than duplicate that AJAX call (and risk a race
    // against _lu-filter.js's own populate-and-empty cycle triggered by the
    // very same state change), this waits for the option _lu-filter.js will
    // load to actually appear, then selects it. Nominatim's county name
    // (e.g. "Los Angeles County") carries a suffix MLMP_LU_County's own
    // Code/Description values don't (just "Los Angeles"), stripped here.
    // Two geocode results can each start their own poll within the same
    // ~3s window (e.g. two drags in quick succession) — this generation
    // counter lets a poll tell whether a newer one has since started, so a
    // slower, now-stale lookup can never overwrite a more recent one.
    let county_poll_generation = 0;

    function set_county_when_options_load(county_name_raw) {
      const $county_field = $sub_field('county_us');
      const $select = $county_field.find('select').first();
      if (!$county_field.length || !$select.length || !county_name_raw) return;

      const county_name = county_name_raw
        .replace(/\s+(County|Parish|Borough|Census Area|Municipality)$/i, '')
        .trim()
        .toLowerCase();

      const my_generation = ++county_poll_generation;
      let attempts = 0;
      const MAX_ATTEMPTS = 20; // ~3s at 150ms

      (function poll() {
        if (my_generation !== county_poll_generation) return;
        attempts++;
        const $match = $select.find('option').filter(function () {
          const $opt = $(this);
          return ($opt.val() || '').toLowerCase() === county_name || ($opt.text() || '').toLowerCase() === county_name;
        }).first();

        if ($match.length) {
          set_field('county_us', $match.val());
          return;
        }
        if (attempts < MAX_ATTEMPTS) {
          setTimeout(poll, 150);
        }
      })();
    }

    function apply_reverse_geocode_address(address) {
      if (!address) return;

      const country_code = (address.country_code || '').toUpperCase();
      const country = country_code in STATE_FIELD_BY_COUNTRY ? country_code : 'OT';
      set_field('country', country);

      const city = address.city || address.town || address.village || address.hamlet || address.municipality || '';
      set_field('city', city);

      set_field('postal_code', address.postcode || '');

      // e.g. "US-CA" -> "CA". Only trust this when it's actually present —
      // no fallback name-matching against the state/province/region choices.
      const iso = address['ISO3166-2-lvl4'] || '';
      const state_field = STATE_FIELD_BY_COUNTRY[country];
      if (iso.indexOf('-') !== -1 && state_field) {
        const code = iso.split('-')[1];
        const $state_field = $sub_field(state_field);
        const $select = $state_field.find('select').first();
        // Only set it if that code is actually one of this dropdown's
        // choices — the ISO code set doesn't perfectly match every list.
        if ($select.length && $select.find('option[value="' + code + '"]').length) {
          set_field(state_field, code);
          // Setting state_us triggers _lu-filter.js's own AJAX populate of
          // county_us (same 'change' event it already listens for) — only
          // relevant for the US, since county_us doesn't apply elsewhere.
          if (country === 'US' && address.county) {
            set_county_when_options_load(address.county);
          }
        }
      }
    }

    // A second drag/click/search before the first request's response
    // arrives starts a second, independent fetch — network delivery isn't
    // guaranteed to preserve request order, so the first (now stale)
    // response can arrive after the second. This generation counter lets a
    // response tell whether a newer request has since started, so a
    // slower, now-stale lookup can never overwrite the current pin
    // position's address — same guard shape as county_poll_generation above.
    let geocode_generation = 0;

    function reverse_geocode(lat, lng) {
      const my_generation = ++geocode_generation;
      fetch(
        'https://nominatim.openstreetmap.org/reverse?format=json&lat=' + lat + '&lon=' + lng + '&addressdetails=1',
        { headers: { 'Accept-Language': 'en' } }
      )
        .then(r => r.json())
        .then(result => {
          if (my_generation !== geocode_generation) return;
          apply_reverse_geocode_address(result && result.address);
        })
        .catch(() => {});
    }

    const debounced_reverse_geocode = debounce(reverse_geocode, 600);

    // A drag can end just a few meters from where the last reverse-geocode
    // already ran (nudging the pin to fine-tune placement) — re-fetching an
    // external address lookup for a move that small is never going to
    // change the result. ~0.0005° is roughly 50m, well under the size of
    // any address/city/state boundary, so this only skips genuinely
    // redundant lookups, not meaningfully different locations.
    const REGEOCODE_MIN_DELTA = 0.0005;
    let last_geocoded_lat = null;
    let last_geocoded_lng = null;

    function maybe_reverse_geocode(lat, lng) {
      if (
        last_geocoded_lat !== null &&
        Math.abs(lat - last_geocoded_lat) < REGEOCODE_MIN_DELTA &&
        Math.abs(lng - last_geocoded_lng) < REGEOCODE_MIN_DELTA
      ) {
        return;
      }
      last_geocoded_lat = lat;
      last_geocoded_lng = lng;
      debounced_reverse_geocode(lat, lng);
    }

    function set_inputs(lat, lng) {
      $lat_input.val(parseFloat(lat.toFixed(7))).trigger('change');
      $lng_input.val(parseFloat(lng.toFixed(7))).trigger('change');
    }

    if (has_initial) {
      place_marker(initial_lat, initial_lng);
    }

    // ── Map click ────────────────────────────────────────────────────────────

    map.on('click', function (e) {
      place_marker(e.latlng.lat, e.latlng.lng);
      set_inputs(e.latlng.lat, e.latlng.lng);
      maybe_reverse_geocode(e.latlng.lat, e.latlng.lng);
    });

    // ── Inputs → Map ─────────────────────────────────────────────────────────

    function sync_from_inputs() {
      const lat = parseFloat($lat_input.val());
      const lng = parseFloat($lng_input.val());
      if (isNaN(lat) || isNaN(lng)) return;
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return;
      place_marker(lat, lng);
      map.setView([lat, lng], map.getZoom() < MARKER_ZOOM ? MARKER_ZOOM : map.getZoom());
    }

    $lat_input.add($lng_input).on('change', sync_from_inputs);

    // ── Real-time proximity check ─────────────────────────────────────────────

    const settings = (typeof mlmp_settings !== 'undefined') ? mlmp_settings : {};
    const $site_id_field = $('.acf-field[data-name="site_id"]');
    const proximity_modal = document.getElementById('site-info-too-close');
    let last_proximity_check = null;

    if (proximity_modal) {
      proximity_modal.querySelector('.modal-dismiss')?.addEventListener('click', function () {
        proximity_modal.classList.remove('is-active');
      });
    }

    const check_proximity = debounce(function () {
      const lat = parseFloat($lat_input.val());
      const lng = parseFloat($lng_input.val());
      if (isNaN(lat) || isNaN(lng) || (!lat && !lng)) return;

      const check_key = lat.toFixed(5) + ',' + lng.toFixed(5);
      if (check_key === last_proximity_check) return;
      last_proximity_check = check_key;

      const site_id = $site_id_field.length ? (parseInt($site_id_field.find('input').val()) || 0) : 0;

      $.post(settings.ajax_url || '', {
        action: 'mlmp_check_site_proximity',
        ajax_nonce: settings.nonce || '',
        lat: lat,
        lng: lng,
        site_id: site_id,
      }, function (response) {
        if (response && response.nearby && proximity_modal) {
          proximity_modal.classList.add('is-active');
        }
      });
    }, 800);

    $lat_input.add($lng_input).on('change', check_proximity);

    // ── Geocoder search ──────────────────────────────────────────────────────

    const $search_input = $search_row.find('input');
    const $search_btn = $search_row.find('button');

    function geocode() {
      const query = $search_input.val().trim();
      // Re-entry while a search is already in flight can't happen — both
      // the input and button are disabled below for the duration, and a
      // disabled input's own keydown handler doesn't fire.
      if (!query) return;

      $search_input.prop('disabled', true);
      $search_btn.prop('disabled', true).text('…');

      fetch('https://nominatim.openstreetmap.org/search?format=json&limit=1&addressdetails=1&q=' + encodeURIComponent(query), {
        headers: { 'Accept-Language': 'en' }
      })
        .then(r => r.json())
        .then(results => {
          if (!results.length) {
            alert('Address not found. Try a more specific search.');
            return;
          }
          // Only invalidate a drag/click-triggered lookup once this search
          // is confirmed to have a real result to apply — doing this
          // unconditionally up front would also kill a legitimate, unrelated
          // drag lookup on every failed/empty search. Cancels a pending
          // (not yet fired) debounced reverse_geocode outright, and bumps
          // geocode_generation so an already in-flight one's response is
          // discarded when it resolves — reverse_geocode() itself has no
          // self-check, so this search's own result is never at risk of
          // being discarded by a concurrent drag the way a shared
          // generation check on both sides would risk.
          debounced_reverse_geocode.cancel();
          geocode_generation++;

          const lat = parseFloat(results[0].lat);
          const lng = parseFloat(results[0].lon);
          place_marker(lat, lng);
          set_inputs(lat, lng);
          map.setView([lat, lng], MARKER_ZOOM);
          last_geocoded_lat = lat;
          last_geocoded_lng = lng;
          apply_reverse_geocode_address(results[0].address);
        })
        .catch(() => alert('Geocoding request failed. Check your connection.'))
        .finally(() => {
          $search_input.prop('disabled', false);
          $search_btn.prop('disabled', false).text('Go');
        });
    }

    $search_btn.on('click', geocode);
    $search_input.on('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); geocode(); }
    });

  });

};
